// REUSABLE: Action Buttons Component
// Standard action buttons for table rows (View, Edit, Delete, etc.). Used by: All table/list views
// Benefits: Consistent action button styling, Built-in confirmation dialogs, Follows existing icon button patterns

import React from 'react';
import Swal from 'sweetalert2';

export interface ActionButton {
  type: 'view' | 'edit' | 'delete' | 'approve' | 'reject' | 'custom'; // Button type
  icon?: string; // Icon class (FontAwesome)
  onClick: () => void; // Click handler
  label?: string; // Button label/tooltip
  confirm?: { // Show confirmation dialog before action
    title: string;
    text: string;
    confirmButtonText?: string;
    icon?: 'warning' | 'question' | 'info';
  };
  disabled?: boolean; // Disable button
  colorClass?: string; // Custom color class
}

interface ActionButtonsProps {
  actions: ActionButton[]; // Array of action buttons to display
  align?: 'start' | 'center' | 'end'; // Alignment (default: center)
  className?: string; // Additional className
}

// Get default icon and color for button type
const getButtonDefaults = (type: ActionButton['type']) => {
  switch (type) {
    case 'view':
      return { icon: 'fa-eye', colorClass: 'btn-active-color-primary' };
    case 'edit':
      return { icon: 'fa-edit', colorClass: 'btn-active-color-info' };
    case 'delete':
      return { icon: 'fa-trash', colorClass: 'btn-active-color-danger' };
    case 'approve':
      return { icon: 'fa-check', colorClass: 'btn-active-color-success' };
    case 'reject':
      return { icon: 'fa-times', colorClass: 'btn-active-color-danger' };
    default:
      return { icon: 'fa-cog', colorClass: 'btn-active-color-primary' };
  }
};

// Action buttons component for table rows
export const ActionButtons: React.FC<ActionButtonsProps> = ({
  actions,
  align = 'center',
  className = '',
}) => {
  const handleClick = async (action: ActionButton) => {
    if (action.disabled) {
      return;
    }

    // Show confirmation dialog if configured
    if (action.confirm) {
      const result = await Swal.fire({
        title: action.confirm.title,
        text: action.confirm.text,
        icon: action.confirm.icon || 'warning',
        showCancelButton: true,
        confirmButtonText: action.confirm.confirmButtonText || 'Yes, proceed',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    // Execute action
    action.onClick();
  };

  const alignClass = 
    align === 'start' ? 'justify-content-start' :
    align === 'end' ? 'justify-content-end' :
    'justify-content-center';

  return (
    <div className={`d-flex ${alignClass} flex-shrink-0 ${className}`}>
      {actions.map((action, index) => {
        const defaults = getButtonDefaults(action.type);
        const icon = action.icon || defaults.icon;
        const colorClass = action.colorClass || defaults.colorClass;

        return (
          <button
            key={index}
            className={`btn btn-icon btn-bg-light ${colorClass} btn-sm me-1`}
            onClick={() => handleClick(action)}
            disabled={action.disabled}
            title={action.label}
          >
            <i className={`fa ${icon}`}></i>
          </button>
        );
      })}
    </div>
  );
};

export default ActionButtons;


